// Maps the BE-1 eventTimeline response onto the shape EventTimeline renders.
//
// Endpoint: GET /amls/common/alerts/{alertId}/eventTimeline
// Contract (be/amlscommon/.../unifiedalerts/model/):
//   EventTimelineResponse { alertId, contextMode, eventCount, truncated, events[] }
//   AlertEvent  { id, type: "Activity"|"Transaction", label, account,
//                 timestamp (ISO-8601), fraudScore, isTrigger,
//                 severity: "Failed"|"Neutral"|"Success", detail }
//   EventDetail — flat union, discriminated by AlertEvent.type:
//                 Activity    -> eventType, details, device, ipAddress
//                 Transaction -> eventType, details, amount, sender,
//                                beneficiary, destination, partner
//
// Pure function, kept out of the container so it can be unit-tested against a
// captured payload with no store and no network.

// severity is already the design's vocabulary, just capitalised.
import { toSectionRows, toSectionTitle } from './panelSections';
import {
  spatialRows,
  groupSpatialRows,
  groupPanelRows,
  identifierRow,
  groupIdentifierRow
} from './fields';
// Shared with the Event Fields settings list so the two never drift.
import { ICON_BY_EVENT_TYPE } from '../eventIcons';
import { SCORE_BAND, scoreBand } from '../../statusColors';
import { eventMoment, formatEventDateTime } from '../../../../../../util/timezone';

const STATUS_BY_SEVERITY = {
  Failed: 'failed',
  Neutral: 'neutral',
  Success: 'success'
};

// detail.eventType -> glyph key in ../eventIcons.js. Unmapped types fall back to
// the type-level default, so a new BE event type degrades to a sensible icon
// rather than disappearing.
//
// REMAPPED 2026-08-23 to the design's own glyphs. It rendered log-in for both
// login events, message-square for the OTP burst, key for BOTH password events
// and landmark for Fund Transfer — where this table had monitor / shield /
// triangle-alert / dollar-sign. Only the four types the design actually shows are
// changed; PROFILE_UPDATE and the two account types keep what they had, since the
// design never renders them and guessing would be worse than leaving them.
//
// THE KEYS BELOW ARE IN TWO GROUPS. The first are VERIFIED against live data —
// read straight out of StarRocks `fincense.activity.activity_type` for
// QA-FRAML-009 on 2026-08-23. The second are UNVERIFIED: they were written from
// assumption before any real payload existed, they match nothing the BE has ever
// been observed to send, and every one of them is a near-miss for a real key
// (OTP_VERIFICATION vs LOGIN_MFA_WITH_OTP, PASSWORD_CHANGE vs PASSWORD_CHANGED).
// They are kept only because another tenant's pipeline might use them; do not add
// more like them, and do not assume a node renders correctly because a plausible
// key appears here.
// The design's node tag reads ACT / TXN.
const TAG_BY_TYPE = { Activity: 'ACT', Transaction: 'TXN' };

// timestamp is ISO-8601 per the contract; parse defensively anyway and fall back
// to the raw string rather than rendering "Invalid Date" on a node.
// FIN-13559 — was `new Date(value)` + browser-local getters. That made the node and the
// panel disagree with the Transaction Information grid, which additionally applies the
// tenant's `time-adjustment`: one API value rendered as three different wall clocks on
// one alert. Both now go through the shared helper, so every surface reads the grid's
// interpretation — the one QA confirmed correct for the record.
const parseTs = (value) => eventMoment(value);


// A node shows DAY/MONTH then the time of day ("14/08 09:41:02").
//
// DECIDED 2026-08-26 (client feedback via user), REVERSING the 2026-08-14 call
// that stripped the date to match the design's bare "09:41:02".
//
// What changed underneath it: grouping no longer has a time window, and the
// timeline deliberately mixes the look-back window with the current event, so a
// node's neighbours are routinely days apart rather than minutes. The accepted
// cost of the bare format was that such a sequence reads as out of order
// (17:18 -> 12:48 -> 08:18) when it is in fact chronological. That cost was
// tolerable when day-spanning was the exception; it is not, now that it is the
// norm.
//
// The date is DD/MM, not DD/MM/YYYY — the year is shared by every event on an
// alert in practice, and a node face has no room for it. The panel's Timestamp
// row keeps the unambiguous full-year form (see formatTimestamp).
const formatTime = (value) => {
  const formatted = formatEventDateTime(value, 'DD/MM HH:mm:ss');
  if (formatted) return formatted;
  return typeof value === 'string' ? value : undefined;
};

const formatTimestamp = (value) => {
  const formatted = formatEventDateTime(value);
  if (formatted) return formatted;
  return typeof value === 'string' ? value : '—';
};

// THE CONTRACT FIELD IS `fraudScore`, AND IT IS ALREADY 0..100.
//
// This read `event.riskScore` and multiplied by 100, on the assumption of a 0..1
// scale. The response carries no such field — it sends `fraudScore` (measured
// live: 76.9, 94.2, 87.5; AlertEvent.scala:9 `fraudScore: Option[Double]`). So
// every score resolved to undefined and, because the badge is gated on a resolved
// band, NO node has ever shown a score on live data. Reading the right field
// while still scaling would have rendered 7690.
//
// `riskScore` is kept as a scaled fallback: the shape was assumed for a reason,
// and a prototype build or an older fixture may still send it. If it ever
// reappears it will be handled rather than silently ignored.
// NOTE: this is unified_alerts.PARTY_CRS_RISK_SCORE — a CUSTOMER-level score, so
// every event in an alert carries the SAME value (see EVENT_TIMELINE_SEED.md).
// ---------------------------------------------------------------------------
// LABEL LENGTH CAP
//
// `activity_description` has no length contract — QA-FRAML-001 carries a row of
// 200 X characters, and one node that wide distorts the whole track. The text is
// NOT rewritten, only capped.
//
// 20 keeps nodes near the design's width (its longest label, "Beneficiary Added",
// is 17) and still leaves the eventType prefix legible:
// "OTP_VERIFICATION vi…", "ACCOUNT_LINKING via…". A 10 cap would give
// "OTP_VERIFI…" / "Boundary r…", which identifies nothing. The trade-off is that
// the status suffix ("(FAILED)") no longer fits — but that is already carried by
// the node's colour and ring.
//
// Nothing is lost — a capped label no longer equals `detail.details`, so the
// full string surfaces in the side panel's Details row, and EventTimeline.js
// puts it on the node's `title` for hover.
// ---------------------------------------------------------------------------
const MAX_LABEL_CHARS = 20;

const truncateLabel = (value) => {
  if (typeof value !== 'string' || value.length <= MAX_LABEL_CHARS)
    return value;
  // -1 for the ellipsis, so the result is exactly MAX_LABEL_CHARS. trimEnd
  // avoids "... word …" reading as a gap.
  return `${value.slice(0, MAX_LABEL_CHARS - 1).trimEnd()}…`;
};

// Absent when the column is NULL, and an absent score must not render as 0 — no
// score is not the same as no risk.
const toScore = (event) => {
  const raw = toRawScore(event);
  return raw === undefined ? undefined : Math.round(raw);
};

/* THE UNROUNDED SCORE, for BANDING ONLY (raised in review).
   `toScore` rounds for display — a node shows "66", not "66.4" — and banding the
   rounded value disagrees with the server near a cut-off. The backend compares
   the raw double:
       if (score <= lowMax) Low else if (score <= mediumMax) Good else High
   (fraud_common ScoringModels.AlertLevelThresholds). So on 33/66 a 66.4 rounds
   to 66, bands Good here and High there; 33.3 rounds to 33, bands Low here and
   Good there. The direction of our comparisons already matches — `> mediumMax`
   is `not <= mediumMax` — so rounding was the whole of the disagreement.
   Displayed values are unchanged; only what the band is computed from. */
const toRawScore = (event) => {
  const fraud = event?.fraudScore;
  if (typeof fraud === 'number') return fraud;
  const risk = event?.riskScore;
  if (typeof risk === 'number') return risk * 100;
  return undefined;
};

// Side-panel rows. `divider: true` renders a separator, matching the design's
// split between event facts and device/counterparty facts.
const toDetailRows = (event) => {
  const d = event.detail || {};
  const rows = [
    // The generic kind ("Activity" / "Transaction"), matching the design and the
    // group panel. NOT detail.eventType — the node label already names the
    // specific type, so "LOGIN" here would just repeat it in enum form.
    { label: 'Event Type', value: event.type || d.eventType || '—' },
    { label: 'Timestamp', value: formatTimestamp(event.timestamp) }
  ];

  const score = toScore(event);
  rows.push({
    label: 'Fraud Score',
    value: score == null ? '—' : String(score)
  });

  // PINNED (FIN-13594): Event Type / Timestamp / Fraud Score / Event ID lead
  // every panel, and the admin's On Click fields follow in the typed section.
  // `link` gives it the primary colour identifiers carry everywhere else.
  const id = identifierRow(event);
  rows.push(
    id
      ? { label: 'Event ID', value: id.value, link: true }
      : { label: 'Event ID', value: '—' }
  );

  // Skipped when it just repeats the node label — the BE currently sends the same
  // string for both (label "PROFILE_UPDATE via API (BLOCKED)" == detail.details),
  // and the panel already shows it as the title.
  // Compared against the DISPLAYED (possibly capped) label — a truncated node is
  // not a duplicate of the full text, so the full text must still show here.
  if (d.details && d.details !== truncateLabel(event.label)) {
    rows.push({ label: 'Details', value: d.details });
  }
  if (event.account) rows.push({ label: 'Account', value: event.account });

  // The typed section — title plus the rows the design lists for THIS eventType,
  // already filtered to what the BE sent. See panelSections.js for why the config
  // names rows we cannot fill yet.
  const sectionRows = toSectionRows(event);
  if (sectionRows.length) {
    rows.push({ divider: true });
    const title = toSectionTitle(event);
    if (title) rows.push({ section: title });
    sectionRows.forEach((row) => rows.push(row));
  }

  return rows;
};

/**
 * §7 — the icon for an event type (FIN-13583).
 *
 * THE ADMIN'S CHOICE NOW DRIVES THE NODE. It still does not arrive with the
 * event: the eventTimeline payload's `detail` is exactly `{ eventType, fields }`
 * with no `iconKey`, so `iconConfig` is fetched separately from
 * `GET /eventFieldConfig/event-types` and threaded in — see ./toIconConfig.js
 * and the container's note on why a second call is acceptable here when the
 * FIELD config's was not.
 *
 * PRECEDENCE, and the reasoning for the middle step:
 *   1. the icon configured FOR THIS TYPE — an explicit choice about this event
 *   2. the built-in per-type glyph
 *   3. the icon configured on the category's System Default row
 *   4. the category fallback
 *
 * STEP 2 SITS ABOVE STEP 3 ON PURPOSE. Setting the System Default to a piggy
 * bank should not turn Login into a piggy bank — the design draws Login as an
 * arrow, OTP as a speech bubble, Password Reset as a key, and a generic default
 * is not a statement about any of them. It only speaks for types nobody has
 * described. That is also what the ticket's own case needs: `mobilewallet` has
 * no configured row AND no built-in glyph (ICON_BY_EVENT_TYPE holds no
 * transaction entries), so it reaches step 3 and renders the configured icon.
 *
 * Step 4 is the story's rule for an unconfigured type — "If no icon is
 * configured, the system must use the default Activity or Transaction icon".
 *
 * MATCHING IS BY `technical_value`, which is free text an admin types. A row
 * whose value does not match what the BE puts in `detail.eventType` simply never
 * matches, and the event falls through to the steps below — wrong-but-harmless
 * rather than wrong-and-loud. Activities agree today (`LOGIN`); transactions
 * carry `transaction_type_description`, which is a description rather than a
 * code, so per-type transaction rows may not match until the two are reconciled.
 */
const iconFor = (eventType, isTransaction, iconConfig) => {
  const category = isTransaction ? 'TRANSACTION' : 'ACTIVITY';
  const configured = iconConfig?.[category] || {};

  return (
    configured.byType?.[eventType] ||
    ICON_BY_EVENT_TYPE[eventType] ||
    configured.fallback ||
    (isTransaction ? 'landmark' : undefined)
  );
};

/**
 * The design's per-node fact lines — up to three `{ label, value }` rows under
 * the timestamp. The node draws only the value; the label goes to the hover
 * (FIN-13566). Design DOM (CONFIRMED 2026-08-14):
 *   <span class="text-[7px] text-muted-foreground mt-0.5 max-w-[104px] truncate">
 *
 * ENTIRELY BACKEND-DRIVEN NOW. The node shows the fields an admin ticked for
 * Spatial Layout, in the order the BE sends them, capped at three — see fields.js
 * for why the list is deduped on the way through.
 *
 * The cap is the story's (§11) and also what the node has room for at 104px. The
 * settings screen enforces it too, so this is a second line of defence.
 *
 * NO FALLBACK LIST. The old hardcoded candidates read `detail.amount` /
 * `detail.device` / `detail.ipAddress`, none of which exist in the contract any
 * more, so they could only ever render nothing. An unconfigured type is not bare
 * either — the BE sends one default field (`id` / `activity_id`) with
 * showSpatial set, so the node keeps an identifier line.
 */
// The identifier plus at most three configured fields — fields.js owns the cap
// (MAX_SPATIAL_USER_FIELDS), so the pinned identifier never takes an admin's slot.
const toNodeLines = (event) => spatialRows(event);

/**
 * Scenarios — the reasons the alert fired. The design pages between them and
 * shows one timeline per scenario.
 *
 * PROPOSED BE-1 SHAPE (not in the contract yet — see the note at the top):
 *   scenarios: [{ id, name, description, variant, score, eventIds: string[] }]
 *
 * `eventIds` REFERENCES the canonical `events` list rather than nesting events,
 * because the design's scenarios OVERLAP — its "Re-Login" and "Fund Transfer"
 * nodes appear under both scenarios. Nesting would duplicate them and break
 * selection (two nodes with one id).
 *
 * Returns undefined when the payload carries no scenarios, which is what the
 * live BE does today. The timeline then renders exactly as before — no selector,
 * every event shown — so this is additive and cannot regress the current view.
 *
 * GROUPED NODES: a group node takes the id of its FIRST member (see toGroup), so
 * a scenario naming that member keeps the whole group. A scenario naming only a
 * LATER member of a burst would lose it — flag to BE-1 rather than silently
 * papering over it, since it would mean the burst spans scenarios.
 */
const toScenarios = (payload, events) => {
  const raw = payload?.scenarios;
  if (!Array.isArray(raw) || !raw.length) return undefined;

  const known = new Set(events.map((e) => e.id));
  return raw
    .filter((s) => s && s.id)
    .map((s) => ({
      id: s.id,
      name: s.name || s.typology || s.id,
      description: s.description,
      variant: s.variant,
      // NOT toScore(): that reads the EVENT's fraudScore. A
      // scenario score is ALREADY 0-100 — the design reads "Score 45"/"Score 37",
      // matching rules[].score (45/37) in alertDetailMock. Running it through
      // toScore would render 4500. Revisit if BE-1 turns out to send 0-1 here.
      score: typeof s.score === 'number' ? Math.round(s.score) : undefined,
      // Ids the BE sent that we are not rendering are dropped here rather than
      // at render time, so `eventIds.length` is a truthful node count for the
      // header badge.
      eventIds: (s.eventIds || []).filter((id) => known.has(id))
    }));
};

// The pill beside the panel title.
//
// MEASURED ON THE SCORE BANDS, not on the event's own status (changed
// 2026-08-23 on request): the card header states High (70+) / Medium (40-69) /
// Low (<40), and the pill has to agree with the legend the analyst is reading and
// with the node badge, which is already banded. A Password Reset scoring 60 reads
// `neutral` even though its activity_status is SUCCESS — the score is the risk
// statement, the status is just what the system did.
//
// The three labels keep the existing vocabulary because SCORE_BAND_COLOR already
// aliases the bands onto exactly these hues: high -> failed's red, medium ->
// neutral's amber, low -> success's green.
const BAND_TAG = {
  [SCORE_BAND.HIGH]: 'failed',
  [SCORE_BAND.MEDIUM]: 'neutral',
  [SCORE_BAND.LOW]: 'success'
};

/**
 * @param score    the node's score (a group passes its peak, as the badge does)
 * @param fallback 'success'|'neutral'|'failed' derived from severity
 * @param isTrigger whether this node raised the alert
 */
const toStatusTag = (score, fallback, isTrigger, bands) => {
  // The trigger outranks its band. TRANSACTIONS ARE HARDCODED severity "Neutral"
  // on the BE, so `flagged` could never be derived from status; and a triggering
  // event is worth naming as such whatever it scored.
  if (isTrigger) return 'flagged';

  const band = scoreBand(score, bands);
  if (band) return BAND_TAG[band];

  // UNSCORED events fall back to severity — 009's "Password changed" carries no
  // score at all, and the design still badges it `success`. `neutral` is dropped
  // here rather than shown: with no score behind it, it states nothing.
  return fallback && fallback !== 'neutral' ? fallback : undefined;
};

const toEvent = (event, triggerEventId, iconConfig, bands) => {
  const d = event.detail || {};
  const label = truncateLabel(event.label);
  return {
    id: event.id,
    // The BE's own text — capped in length only, never rewritten. See the NODE
    // LABELS note and MAX_LABEL_CHARS.
    label,
    // Untruncated, for the node's hover title.
    fullLabel: event.label,
    time: formatTime(event.timestamp),
    score: toScore(event),
    // Unrounded, for banding — see toRawScore.
    rawScore: toRawScore(event),
    status: STATUS_BY_SEVERITY[event.severity] || 'neutral',
    statusTag: toStatusTag(
      toRawScore(event),
      STATUS_BY_SEVERITY[event.severity],
      Boolean(triggerEventId) && event.id === triggerEventId,
      bands
    ),
    icon: iconFor(d.eventType, event.type === 'Transaction', iconConfig),
    type: TAG_BY_TYPE[event.type] || event.type,
    // The red ring on the node — the BE now says which event triggered the alert.
    // THE TRIGGER PILL MARKS ONE EVENT: the one that raised the alert (lead + PM,
    // 2026-08-21, matching the design — only Fund Transfer carries it, the login,
    // the OTP burst and the password reset before it do not).
    //
    // DELIBERATELY NOT the BE's `isTrigger`, which is true for EVERY event under
    // contextMode=triggered because they all come out of triggered_activity /
    // triggered_transactions. Tagging all of them made the pill meaningless — it
    // marked the whole row rather than the culminating event. `isTrigger` means
    // "participated in a rule firing"; this pill means "created this alert", and
    // they are not the same thing.
    //
    // Unknown trigger id -> nothing is tagged. Falling back to `isTrigger` would
    // reinstate the all-nodes-tagged behaviour this replaces, and inventing a tag
    // is worse than omitting one on a fraud timeline.
    triggered: Boolean(triggerEventId) && event.id === triggerEventId,
    // NO AMOUNT PILL. `detail.amount` was removed from the contract by
    // FIN-12628 and there is no successor: `custom_field` exposes THREE
    // configurable amounts (sender_transaction_currency_amount,
    // sender_amount_usd, receiver_transaction_currency_amount), so there is no
    // principled way to pick one to promote. Amount now renders as an ordinary
    // configured line whenever an admin ticks it for Spatial Layout.
    // Restoring the pill needs a semantic flag on the wire — raised with BE.
    // The one-liner revealed by the timeline's expand toggle. Suppressed when it
    // duplicates the label the node already shows.
    //
    // Compared against the UNTRUNCATED `event.label`, not the capped `label`
    // above. For activities the BE sets both the label and detail.details from
    // activityDescription, so they are the same string — but `label` has already
    // been cut to MAX_LABEL_CHARS, the equality missed, and every activity node
    // rendered its own title again as a grey second line.
    summary: d.details && d.details !== event.label ? d.details : undefined,
    nodeLines: toNodeLines(event),
    details: toDetailRows(event)
    // NOT mapped — the contract has no source for them:
    //   children[]  — the grouped "N EVENTS" list
    //   multiplier  — the "ACT x5" badge
    // Those surfaces stay empty on live data until the BE exposes them.
  };
};

// ---------------------------------------------------------------------------
// GROUPING — collapsing a burst of same-type events into one node ("ACT x5").
//
// Spec from the team lead's worked example:
//   Txn1 Fund Transfer 10:00 · Act1-4 Login Attempt 10:01-10:04 (mixed statuses)
//   · Act6 Beneficiary Add 10:03 (interleaved) · Act5 OTP Attempt 10:10
//   -> Fund Transfer · Login Attempt (x4) · Beneficiary Add · OTP Attempt
//
// Three rules follow from it:
//   1. group by detail.eventType — NOT by label. `label` is
//      `activity_description`, free text from ingestion; one event on
//      QA-FRAML-001 is labelled "Boundary row dated exactly on alertDate" while
//      its eventType is PASSWORD_RESET. eventType is already in the contract,
//      so grouping needs no backend change.
//   2. statuses may differ inside a group (his four logins are F/F/S/F).
//   3. (superseded by FIN-13861) an interleaved event of ANOTHER type DOES end
//      the run: only CONSECUTIVE same-type events group.
// ---------------------------------------------------------------------------

// NO TIME WINDOW — settled 2026-08-26 (client feedback via user), replacing the
// 15-minute placeholder that stood here awaiting exactly this answer.
//
// Same-type events group HOWEVER FAR APART they are: the worked example is an
// activity on 14 Aug and the next of that type on 20 Aug, which must collapse to
// ONE node reading "14/08 14:35–20/08 14:47". A gap is no longer evidence of a
// separate episode, because the timeline deliberately mixes the look-back window
// with the current event — days between two logins is the normal shape, not an
// outlier.
//
// Rule 3 is UNCHANGED and was re-confirmed with the user when this landed: an
// interleaved event of another type still does NOT split the run. Grouping stays
// keyed on eventType, so each type keeps its own independent run and a
// Beneficiary Add between two Logins leaves the Logins as one group.
// `toEvents.grouping.test.js` pins that behaviour — it is the team lead's spec.
//
// CONSEQUENCE, accepted: under `contextMode: 'full'` (~a month of history) a
// party with many same-type events and nothing interleaved collapses to very few
// nodes. That is the requested behaviour — the count and the span carry what the
// separate nodes used to.

// Two events are the minimum that can be described as "x2".
const MIN_GROUP_SIZE = 2;

// Worst-first: a group containing a failure must read as failed. Averaging to
// neutral would hide the very thing that makes it worth looking at.
const STATUS_RANK = { failed: 3, neutral: 2, success: 1 };

// TRANSACTIONS GROUP TOO, since 2026-08-21. They were excluded on the grounds
// that collapsing payments hides their amounts — the one thing an analyst most
// needs — and the lead's worked example had only one transaction to go on. PRD §7
// settles it the other way: the criteria say "the same activity_type or txn_type",
// the group node is specified as "ACT xN or TXN xN", and two acceptance criteria
// name grouped Transaction nodes explicitly.
//
// The amount objection is answered rather than dismissed: the collapsed node
// carries the group TOTAL, and every child's own amount is listed when it is
// expanded. Nothing about the money is lost, it moves one click away.
// Exempting the TRIGGERING event from grouping was tried and REVERTED
// (2026-08-21). PRD §7 requires the opposite: "If the trigger is inside a grouped
// event, the collapsed group indicates that it contains the trigger" and
// "expanding the group identifies the exact triggering child event". So the
// trigger groups like anything else, and the group advertises that it holds it.
const isGroupable = (event) => Boolean(event.detail?.eventType);

// LOGIN -> "Login", OTP_VERIFICATION -> "OTP Verification".
// Used as the fallback for group types the design does not name (see GROUP_COPY)
// and to keep acronyms from rendering as "Otp Verification".
const ACRONYMS = new Set([
  'OTP',
  'IP',
  'ID',
  'API',
  'SMS',
  'USSD',
  'KYC',
  'ACH',
  'ATM'
]);
const titleiseEventType = (eventType) =>
  String(eventType)
    .split('_')
    .filter(Boolean)
    .map((word) =>
      ACRONYMS.has(word.toUpperCase())
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join(' ');

const hhmm = (value) => formatEventDateTime(value, 'HH:mm') || undefined;

// ---------------------------------------------------------------------------
// GROUP COPY — the design's wording for a collapsed burst.
//
// The Lovable prototype (alert FS-OTP-BENF-101) names the node
// "Failed OTP Attempts" and summarises it "5 consecutive OTP failures". Neither
// is derivable from the OTP_VERIFICATION enum: "Failed" is a status and
// "Attempts"/"failures" are editorial. So they come from this table.
//
// ONLY types the design actually names get bespoke copy — everything else falls
// back to titleiseEventType. Forcing the pattern would produce
// "Beneficiary Added Attempts", which reads as a bug.
//
// CONTRADICTS the lead's worked example, which labels four mixed-status logins
// "Login Attempt (x4)" — singular, no prefix. Taking the design as authoritative
// since it is the visual spec; his example was about WHICH events group.
// Raised with him; reverting means editing this table only.
// ---------------------------------------------------------------------------
const GROUP_COPY = {
  OTP_VERIFICATION: { noun: 'OTP', plural: 'OTP Attempts' },
  LOGIN: { noun: 'Login', plural: 'Login Attempts' }
};

// NODE LABELS — single nodes show the BE's `label` VERBATIM.
//
// A static eventType -> display-name table was tried and REVERTED (2026-08-13):
// hardcoding product copy in the FE means a new activity type from any client
// pipeline needs a frontend release to read correctly, and the wording cannot be
// varied per client or translated. If the ingestion text is unpresentable
// ("LOGIN via WEB (SUCCESS)"), that is a data/contract problem to fix at the
// source — not something to paper over with a lookup here.
//
// Grouped nodes are different and DO use GROUP_COPY: a group has no BE label at
// all, so there is nothing to pass through.

const groupLabel = (eventType, status) => {
  const copy = GROUP_COPY[eventType];
  if (!copy) return titleiseEventType(eventType);
  return status === 'failed' ? `Failed ${copy.plural}` : copy.plural;
};

// "5 consecutive OTP failures". Undefined for types with no design wording —
// the panel then falls back to the plain event count.
//
// COUNTS FAILURES, NOT MEMBERS. The design's burst is 5-for-5 failed, so the two
// were the same number there and the distinction never showed. Mixed groups are
// the normal case (the lead's example is F/F/S/F), and calling four events
// "4 consecutive failures" when one succeeded overstates the case on a fraud
// alert. "Consecutive" is also only true when nothing succeeded in between.
const groupSummary = (eventType, count, failedCount) => {
  const copy = GROUP_COPY[eventType];
  if (!copy) return undefined;
  if (failedCount === 0) return `${count} consecutive ${copy.noun} events`;
  if (failedCount === count)
    return `${count} consecutive ${copy.noun} failures`;
  return `${count} ${copy.noun} attempts, ${failedCount} failed`;
};

// Full date for the side panel's Timestamp row ("05/11/2023"), which the design
// shows alongside the span rather than a single instant.
const dateOnly = (value) => formatEventDateTime(value, 'DD/MM/YYYY') || undefined;

// Compact day+month for a node face ("14/08"), where the full year does not fit
// and adds nothing — every event in an alert shares it in practice.
const dayMonth = (value) => formatEventDateTime(value, 'DD/MM') || undefined;

// Does this run cross a calendar day? Grouping ignores time entirely now, so a
// group can span days — and when it does, a bare "14:35–14:47" is actively
// misleading, reading as a twelve-minute burst rather than six days.
//
// Compared on the calendar date, NOT on elapsed hours: 23:50 and 00:10 are
// twenty minutes apart but belong to different days, and showing both dates is
// what makes that legible.
const spansDays = (members) => {
  const first = dateOnly(members[0]?.timestamp);
  const last = dateOnly(members[members.length - 1]?.timestamp);
  return Boolean(first && last && first !== last);
};

/**
 * Walk the ordered list and bundle CONSECUTIVE same-type events into one run,
 * whatever the time gap. An event of another type closes the run (FIN-13861,
 * PRD "Repeated-event grouping").
 * @returns {Array<object|{members:object[]}>} singles and groups, in order
 */
const bundle = (ordered) => {
  const out = [];
  // The run currently being extended. An event of another type (or an
  // ungroupable one) closes it, so the same type later starts a NEW run
  // (FIN-13861: "same-type events separated by another event are NOT grouped").
  let run = null;

  ordered.forEach((event) => {
    if (!isGroupable(event)) {
      out.push(event);
      run = null;
      return;
    }

    // FIN-13893: a transaction groups on its LABEL, the thing the analyst reads,
    // so two types an admin gave one display name still group. eventType stays
    // the icon/config key. Activities keep eventType: their label is free text.
    const key =
      event.type === 'Transaction' && event.label
        ? event.label
        : event.detail.eventType;
    if (run && run.key === key) {
      run.members.push(event);
      return;
    }

    run = { key, members: [event] };
    out.push(run);
  });

  // A run of one is just an event.
  return out.map((entry) =>
    entry.members
      ? entry.members.length >= MIN_GROUP_SIZE
        ? { members: entry.members }
        : entry.members[0]
      : entry
  );
};

/** Build the collapsed node from its members (already mapped children). */
/**
 * Fact lines for a COLLAPSED group.
 *
 * A VALUE APPEARS ONLY WHEN EVERY MEMBER AGREES — see groupUserRows in
 * fields.js for why. A burst is one actor on one device, so agreement is the
 * normal case; printing the first member's value on behalf of all of them would
 * state something false on a screen whose whole job is evidence.
 *
 * A contradictory field keeps its line and reads "Multiple values" (FIN-13756),
 * so the node never hides that a field was configured. The identifier is the
 * exception — pinned, it shows the first member's id with every member's in the
 * hover (groupIdentifierRow).
 */
const toGroupLines = (members) => groupSpatialRows(members);

const toGroup = (members, triggerEventId, iconConfig, bands) => {
  // Children carry their own trigger flag so the expanded sub-event list can mark
  // the EXACT triggering occurrence — PRD §7: "Expanding the group identifies the
  // exact triggering child event."
  const multiDay = spansDays(members);
  // Child rows need no special casing: formatTime dates EVERY node now
  // (2026-08-26), so an expanded run reads "14/08 14:35:00 / 20/08 14:47:00"
  // without help. Prefixing here as well would double the date.
  const children = members.map((m) =>
    toEvent(m, triggerEventId, iconConfig, bands)
  );
  const first = members[0];
  const last = members[members.length - 1];
  const eventType = first.detail.eventType;

  const scores = children.map((c) => c.score).filter((s) => s != null);
  /* The peak UNROUNDED, for the group's band and status pill. `scores` above is
     the rounded set the badge DISPLAYS; banding from it disagrees with the
     server near a cut-off exactly as it did for a single node. */
  const rawScores = children.map((c) => c.rawScore).filter((s) => s != null);
  const status = children.reduce(
    (worst, c) =>
      (STATUS_RANK[c.status] || 0) > (STATUS_RANK[worst] || 0)
        ? c.status
        : worst,
    'success'
  );

  const from = hhmm(first.timestamp);
  const to = hhmm(last.timestamp);

  // NO GROUP TOTAL, for the same reason the pill is gone: the mapper can no
  // longer tell which configured field is money, and summing an arbitrary
  // numeric field would happily total risk scores or attempt counts.
  //
  // "GROUPED TRANSACTIONS MUST NOT HIDE THE MONEY" — the objection that kept
  // transactions ungrouped until PRD §7 settled it — is still answered, but by
  // the PARTS rather than the total: each child row now carries its own
  // configured fields (EventTimeline.js), so expanding a burst shows every
  // payment's amount whenever an admin has ticked one.
  //
  // The collapsed total needs a semantic flag on the wire to come back. Raised
  // with BE alongside the icon and isIdentifier gaps.

  // A GROUP HAS NO BE LABEL, so one is generated. TRANSACTIONS are the exception:
  // their label is `transaction_type_description` — a TYPE-level description, the
  // same for every row of that type — so when the members agree it is real client
  // data and reads far better than a titleised code ("ACH Credit", not "ACH").
  //
  // ACTIVITIES are deliberately excluded. Their label is `activity_description`,
  // free text PER EVENT ("LOGIN via WEB (SUCCESS)"), so borrowing it would title a
  // group of five with one member's sentence. They keep the generated copy, and
  // the titleised-enum fallback the design relies on for unnamed types.
  const sharedLabels = new Set(
    members.map((m) => m.label).filter((l) => typeof l === 'string' && l)
  );
  const sharedLabel =
    first.type === 'Transaction' && sharedLabels.size === 1
      ? [...sharedLabels][0]
      : undefined;

  const summary = groupSummary(
    eventType,
    members.length,
    children.filter((c) => c.status === 'failed').length
  );

  // Design's Timestamp row: full date + the span ("05/11/2023 09:42–09:45").
  const spanTimestamp = multiDay
    ? `${dateOnly(first.timestamp)} ${from} – ${dateOnly(last.timestamp)} ${to}`
    : from && to && from !== to
    ? `${dateOnly(first.timestamp)} ${from}–${to}`
    : formatTimestamp(first.timestamp);

  // Device / IP come from the burst's first member. They are per-event fields, so
  // this is only meaningful because a burst is one actor on one device — which is
  // exactly the pattern that makes it worth collapsing. If members ever disagree
  // the panel would be misleading, so take them only when they are consistent.
  // The group's typed section — the SAME On Click fields a single node shows,
  // reduced to what every member agrees on. This was hardcoded Device / IP
  // Address read from `detail.device` / `detail.ipAddress` until 2026-09-01;
  // FIN-12628 removed both keys, so the section had silently gone empty and the
  // group panel showed only Event Type / Timestamp / Fraud Score / Events.
  const sectionRows = groupPanelRows(members);
  const sectionTitle = toSectionTitle(first);
  const groupId = groupIdentifierRow(members);

  return {
    // The group sits where its first member did, and keeps that id so selection
    // and React keys stay stable.
    id: first.id,
    // NOT capped: group labels are generated here, not supplied by the BE, so
    // they are already short and bounded. Capping our own copy would render
    // "Failed Login Attemp…", which is our bug, not a data problem.
    label: sharedLabel || groupLabel(eventType, status),
    // The design reads "09:42–09:45" on a grouped node. An en dash, not a hyphen.
    // Undated, for the same reason single nodes are — see formatTime.
    // Always led by a date, matching the single-node rule in formatTime. The
    // CLOSING date is added only when the run actually crosses a day —
    // "14/08 14:35–20/08 14:47" for the client's worked example, but
    // "07/08 10:00–18:01" for a run inside one day, where repeating 07/08 would
    // be noise. A one-instant run falls through to the single-node format.
    time:
      from && to && from !== to
        ? multiDay
          ? `${dayMonth(first.timestamp)} ${from}–${dayMonth(
              last.timestamp
            )} ${to}`
          : `${dayMonth(first.timestamp)} ${from}–${to}`
        : toEvent(first).time,
    // The badge shows the burst's peak, matching the design (72 over 35/45/55/65/72).
    score: scores.length ? Math.max(...scores) : undefined,
    rawScore: rawScores.length ? Math.max(...rawScores) : undefined,
    status,
    // Same rule as a single node, fed the group's PEAK score — the figure its
    // badge already shows. Worst-wins `status` stays the unscored fallback.
    statusTag: toStatusTag(
      rawScores.length ? Math.max(...rawScores) : undefined,
      status,
      Boolean(triggerEventId) && members.some((m) => m.id === triggerEventId),
      bands
    ),
    // Same fallback chain a SINGLE node uses. Missing it made a grouped
    // transaction render the generic ApartmentOutlined glyph instead of the
    // dollar sign, because ICON_BY_EVENT_TYPE has no transaction entries — only
    // visible once transactions could group at all (2026-08-21).
    nodeLines: toGroupLines(members),
    icon: iconFor(eventType, first.type === 'Transaction', iconConfig),
    type: TAG_BY_TYPE[first.type] || first.type,
    // ANY member triggering makes the group a trigger — hiding a trigger inside
    // a collapsed node would remove the only marker saying it fed the alert.
    // PRD §7: a collapsed group must INDICATE that it contains the trigger. The
    // group inherits the flag from whichever child holds it, which drives the same
    // halo and TRIGGER pill a single node gets.
    triggered:
      Boolean(triggerEventId) && members.some((m) => m.id === triggerEventId),
    multiplier: members.length,
    // The one-liner under the node, matching the design's "5 consecutive OTP
    // failures".
    summary,
    children,
    // Design's side-panel order: Event Type / Timestamp / Fraud Score / Details,
    // divider, then Device + IP Address. Event Type is the generic "Activity" —
    // the enum is already spelled out in the node label.
    //
    // NOT INCLUDED — the design also shows "Location: Bangkok, Thailand".
    // `EventDetail` is now just { eventType, fields }, so location can only
    // arrive as a configured field; if a tenant registers one it appears in the
    // typed section below with no code change. A fabricated location on a fraud
    // alert is not an acceptable stand-in.
    details: [
      { label: 'Event Type', value: first.type || '—' },
      { label: 'Timestamp', value: spanTimestamp },
      {
        label: 'Fraud Score',
        value: scores.length ? String(Math.max(...scores)) : '—'
      },
      // Pinned, as on a single event: the first member's id, every member's in
      // the hover — see groupIdentifierRow.
      groupId
        ? {
            label: 'Event ID',
            value: groupId.value,
            link: true,
            ...(groupId.title ? { title: groupId.title } : {})
          }
        : { label: 'Event ID', value: '—' },
      // Falls back to the bare count for types with no design wording.
      summary
        ? { label: 'Details', value: summary }
        : { label: 'Events', value: String(members.length) },
      ...(sectionRows.length
        ? [
            { divider: true },
            ...(sectionTitle ? [{ section: sectionTitle }] : []),
            ...sectionRows
          ]
        : [])
    ]
  };
};

/**
 * @param {object} payload EventTimelineResponse
 * @returns {{total:number, events:Array, truncated:boolean, contextMode:string}}
 */
/**
 * The per-type field configuration is no longer a parameter: the BE resolves it
 * and embeds the result in each event's `detail.fields[]`, so there is nothing
 * for the caller to fetch or thread through. See fields.js.
 */
export const toEvents = (payload, triggerEventId, iconConfig, bands) => {
  // Tolerate a bare array too — the contract returns an object, but an older
  // build (or a mock) may hand over the list directly.
  const list = Array.isArray(payload) ? payload : payload?.events;
  if (!Array.isArray(list)) return { total: 0, events: [] };

  // The design reads left-to-right oldest-first. The DAO currently sorts DESC
  // (`sortBy(_.timestamp)(Ordering.String.reverse)`), but SORT here rather than
  // reversing: a blind reverse encodes a bet on the BE's ORDER BY and silently
  // inverts the entire timeline if that query ever changes. Sorting makes
  // oldest-first an invariant of our OUTPUT instead of a property of the input.
  // Events with an unparseable/absent timestamp cannot be placed chronologically,
  // so they keep their relative order at the end rather than being scattered.
  //
  // STRICT TIMESTAMP ORDER. Pinning the triggering event last was tried and
  // REVERTED (2026-08-21): PRD §1 requires "sorted by timestamp in ascending
  // order", and §8 rule 2 forbids the UI inferring the trigger from the last
  // event — pinning encoded exactly that inference in the sort. The lead's
  // "triggering happens only on the last node" describes the DESIGN'S DATA, where
  // the trigger genuinely is newest; it is not an ordering instruction.
  const ordered = list
    .filter((e) => e && e.id)
    // FIN-13559: parseTs now yields a moment, so valueOf() — not getTime().
    .map((e, index) => ({ e, index, t: parseTs(e.timestamp)?.valueOf() }))
    .sort((a, b) => {
      if (a.t == null && b.t == null) return a.index - b.index;
      if (a.t == null) return 1;
      if (b.t == null) return -1;
      return a.t - b.t || a.index - b.index;
    })
    .map(({ e }) => e);

  // Collapse bursts of same-type events before mapping — a group is one node.
  const events = bundle(ordered).map((entry) =>
    entry.members
      ? toGroup(entry.members, triggerEventId, iconConfig, bands)
      : toEvent(entry, triggerEventId, iconConfig, bands)
  );

  return {
    // THE EVENT COUNT, NOT THE NODE COUNT. PRD §1 asks the header for the
    // "Total event count — e.g. 6 events", and its acceptance criterion is
    // explicit: "Six contributing events exist … the header displays the
    // applicable event count, e.g. 6 events."
    //
    // This used to fall back to the node count once grouping collapsed
    // anything, on the reasoning that a count which disagreed with the visible
    // rows "overstated" them. That was wrong on two counts: §7 says grouping
    // "changes only presentation and must not change scenario evidence", and
    // the number of contributing events IS evidence — reporting 5 for 13 events
    // understates the alert. The per-group counts are already on the nodes
    // themselves (ACT ×5), so the header is free to be the total.
    total: payload?.eventCount ?? ordered.length,
    events,
    // True when the BE capped the list — worth surfacing so the timeline does not
    // silently imply it is showing everything.
    truncated: Boolean(payload?.truncated),
    contextMode: payload?.contextMode,
    scenarios: toScenarios(payload, events)
  };
};

export default toEvents;
